import { useState, useEffect } from "react";
import api from "../api/client";
import {
  X,
  Send,
  ExternalLink,
  Calendar,
  FileText,
  FlaskConical,
  MessageCircle,
  Clock,
  Users,
} from "lucide-react";
import LoadingSpinner from "./LoadingSpinner";
import { statusClasses } from "../constants/statusColors";

const VALID_STATUSES = [
  "Applied",
  "Screening",
  "Interview",
  "Technical test",
  "Final interview",
  "Offer received",
  "Accepted",
  "Rejected",
  "Withdrawn",
];

const ACTION_ICONS = {
  meeting: Calendar,
  document_request: FileText,
  assessment: FlaskConical,
  info: MessageCircle,
};

// A meeting time or deadline already in the past is almost always a typo.
// Checked on push because the min attribute only greys out the picker in
// some browsers and never blocks a typed value.
const actionHasPastTime = (a) => {
  const value =
    a.action_type === "meeting"
      ? a.meeting_at
      : a.action_type === "info"
        ? null
        : a.deadline;
  return Boolean(value) && new Date(value) < new Date();
};

// A meeting without a time, or a request or assessment without a deadline,
// would reach the candidate as an empty promise.
const actionMissingTime = (a) => {
  if (a.action_type === "meeting") return !a.meeting_at;
  if (a.action_type === "document_request" || a.action_type === "assessment")
    return !a.deadline;
  return false;
};

const inputClass =
  "w-full px-3 py-2 rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface text-sm focus:outline-none focus:ring-2 focus:ring-brand-violet/30 dark:focus:ring-brand-violet-on-dark/30";

// datetime-local gives "2026-09-28T10:00" with no timezone. Converting
// through Date reads it as the employer's local time and sends UTC, so the
// candidate sees the same moment in their own timezone.
const toUtcIso = (value) => (value ? new Date(value).toISOString() : null);

// Earliest selectable time for meeting and deadline inputs, in the
// yyyy-mm-ddThh:mm form datetime-local expects, in local time.
const nowLocalInput = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export default function StatusPushPanel({
  submission,
  authConfig,
  onClose,
  onStatusPushed,
}) {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [newStatus, setNewStatus] = useState("Screening");
  const [note, setNote] = useState("");
  const [actions, setActions] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await api.get(
          `/employer/connected-candidates/${submission.application_id}/history`,
          authConfig,
        );
        setHistory(res.data?.statuses || []);
      } catch (err) {
        // History fetch failures are non-fatal, render empty state.
        // Real auth failures (401) will be handled by the global
        // interceptor; everything else here just means "no history
        // available right now."
        if (err.response?.status >= 500) {
          console.error("History fetch failed:", err);
        }
        setHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
  }, [submission.application_id]);

  const addAction = () => {
    setActions([
      ...actions,
      {
        action_type: "meeting",
        title: "",
        description: "",
        meeting_at: "",
        duration_minutes: "",
        attendees: "",
        url: "",
        deadline: "",
        guidelines: "",
      },
    ]);
  };

  const updateAction = (idx, field, value) => {
    setActions(
      actions.map((a, i) => (i === idx ? { ...a, [field]: value } : a)),
    );
  };

  const removeAction = (idx) => {
    setActions(actions.filter((_, i) => i !== idx));
  };

  // Every attached action needs a title, or it renders as an empty card.
  const actionsValid = actions.every(
    (a) => a.title.trim() && !actionMissingTime(a),
  );

  const handlePush = async () => {
    if (actions.some(actionHasPastTime)) {
      setError("Meeting times and deadlines can't be in the past.");
      return;
    }
    setPushing(true);
    setError(null);
    try {
      await api.post(
        `/employer/applications/${submission.application_id}/status`,
        {
          status: newStatus,
          note: note.trim() || null,
          actions: actions.map((a) => ({
            action_type: a.action_type,
            title: a.title.trim(),
            description: a.description || null,
            meeting_at: toUtcIso(a.meeting_at),
            duration_minutes: a.duration_minutes
              ? parseInt(a.duration_minutes)
              : null,
            attendees: a.attendees
              ? a.attendees
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [],
            url: a.url || null,
            deadline: toUtcIso(a.deadline),
            guidelines: a.guidelines || null,
          })),
        },
        authConfig,
      );
      onStatusPushed();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setPushing(false);
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1" />
      <div
        className="w-full max-w-md bg-white dark:bg-brand-dark-bg shadow-2xl overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-brand-dark-bg border-b border-gray-200 dark:border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-brand-violet/10 text-brand-violet dark:text-brand-violet-on-dark flex items-center justify-center font-semibold flex-shrink-0">
              {submission.candidate_initial}
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate">
                {submission.candidate_full_name ||
                  `Candidate ${submission.candidate_initial}`}
              </p>
              {submission.resume_slug && (
                <a
                  href={`/r/${submission.resume_slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline flex items-center gap-1"
                >
                  View resume
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Push form */}
        <div className="px-6 py-5 border-b border-gray-200 dark:border-white/10">
          <h3 className="text-sm font-semibold mb-3">Push status update</h3>
          <div className="space-y-3">
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className={inputClass}
            >
              {VALID_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note for the candidate"
              rows={3}
              className={inputClass}
            />

            {/* Correspondence actions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Correspondence actions
                </span>
                <button
                  type="button"
                  onClick={addAction}
                  className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                >
                  + Add action
                </button>
              </div>
              {actions.length === 0 && (
                <p className="text-xs text-gray-400 italic">
                  Attach a meeting invite, document request, assessment or
                  information to this update.
                </p>
              )}
              {actions.map((action, idx) => (
                <div
                  key={idx}
                  className="bg-gray-50 dark:bg-brand-dark-surface rounded-xl p-4 mb-3 border border-gray-100 dark:border-white/10"
                >
                  <div className="flex items-center justify-between mb-3">
                    <select
                      value={action.action_type}
                      onChange={(e) =>
                        updateAction(idx, "action_type", e.target.value)
                      }
                      className={`${inputClass} w-auto`}
                    >
                      <option value="meeting">Meeting invite</option>
                      <option value="document_request">Document request</option>
                      <option value="assessment">Assessment</option>
                      <option value="info">Information</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeAction(idx)}
                      className="text-gray-300 hover:text-red-500 transition ml-2"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex flex-col gap-2">
                    <input
                      value={action.title}
                      onChange={(e) =>
                        updateAction(idx, "title", e.target.value)
                      }
                      placeholder="Title *"
                      className={inputClass}
                    />
                    <textarea
                      value={action.description}
                      onChange={(e) =>
                        updateAction(idx, "description", e.target.value)
                      }
                      placeholder="Description"
                      rows={2}
                      className={`${inputClass} resize-none`}
                    />

                    {action.action_type === "meeting" && (
                      <>
                        <input
                          type="datetime-local"
                          min={nowLocalInput()}
                          value={action.meeting_at}
                          onChange={(e) =>
                            updateAction(idx, "meeting_at", e.target.value)
                          }
                          className={inputClass}
                        />
                        <label className="block text-xs text-gray-500 dark:text-gray-400">
                          {action.action_type === "meeting" ? "Date and time *" : "Deadline *"}
                        </label>
                        <input
                          value={action.duration_minutes}
                          onChange={(e) =>
                            updateAction(
                              idx,
                              "duration_minutes",
                              e.target.value,
                            )
                          }
                          placeholder="Duration (minutes)"
                          type="number"
                          className={inputClass}
                        />
                        <input
                          value={action.attendees}
                          onChange={(e) =>
                            updateAction(idx, "attendees", e.target.value)
                          }
                          placeholder="Attendees (comma separated)"
                          className={inputClass}
                        />
                      </>
                    )}
                    {action.action_type === "assessment" && (
                      <input
                        value={action.url}
                        onChange={(e) =>
                          updateAction(idx, "url", e.target.value)
                        }
                        placeholder="Assessment URL"
                        className={inputClass}
                      />
                    )}
                    {(action.action_type === "document_request" ||
                      action.action_type === "assessment") && (
                      <>
                        <label className="block text-xs text-gray-500 dark:text-gray-400">
                          {action.action_type === "meeting" ? "Date and time *" : "Deadline *"}
                        </label>
                        <input
                          type="datetime-local"
                          min={nowLocalInput()}
                          value={action.deadline}
                          onChange={(e) =>
                            updateAction(idx, "deadline", e.target.value)
                          }
                          className={inputClass}
                        />
                        <input
                          value={action.guidelines}
                          onChange={(e) =>
                            updateAction(idx, "guidelines", e.target.value)
                          }
                          placeholder="Guidelines"
                          className={inputClass}
                        />
                      </>
                    )}
                    {action.action_type === "info" && (
                      <input
                        value={action.url}
                        onChange={(e) =>
                          updateAction(idx, "url", e.target.value)
                        }
                        placeholder="Link URL (optional)"
                        className={inputClass}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handlePush}
              disabled={pushing || !actionsValid}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-violet text-white text-sm rounded-md hover:opacity-90 disabled:opacity-50 transition"
            >
              <Send className="w-4 h-4" />
              {pushing ? "Pushing..." : "Push to candidate"}
            </button>
            {!actionsValid && (
              <p className="text-xs text-gray-400">
                Every action needs a title. Meetings need a date and time, and
                requests and assessments need a deadline.
              </p>
            )}
            {error && (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
          </div>
        </div>

        {/* History */}
        <div className="px-6 py-5 flex-1">
          <h3 className="text-sm font-semibold mb-3">Status history</h3>
          {loadingHistory ? (
            <LoadingSpinner />
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No history yet.
            </p>
          ) : (
            <div className="space-y-3">
              {history.map((h) => (
                <div
                  key={h.id}
                  className="border-l-2 border-brand-violet/30 dark:border-brand-violet-on-dark/30 pl-3 py-1"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusClasses(h.status)}`}
                    >
                      {h.status}
                    </span>
                    <span className="text-xs text-gray-400">
                      {formatTime(h.created_at)}
                    </span>
                  </div>
                  {h.note && (
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {h.note}
                    </p>
                  )}

                  {/* Correspondence attached to this status */}
                  {h.actions?.length > 0 && (
                    <div className="flex flex-col gap-1.5 mt-2">
                      {h.actions.map((a) => {
                        const Icon = ACTION_ICONS[a.action_type] ?? MessageCircle;
                        return (
                          <div
                            key={a.id}
                            className="bg-gray-50 dark:bg-brand-dark-surface rounded-lg p-2.5 border border-gray-100 dark:border-white/10"
                          >
                            <div className="flex items-start gap-2">
                              <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-gray-900 dark:text-white">
                                  {a.title}
                                </p>
                                {a.description && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {a.description}
                                  </p>
                                )}
                                {a.meeting_at && (
                                  <p className="text-xs text-brand-violet dark:text-brand-violet-on-dark mt-0.5 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatTime(a.meeting_at)}
                                    {a.duration_minutes &&
                                      ` · ${a.duration_minutes} min`}
                                  </p>
                                )}
                                {a.attendees?.length > 0 && (
                                  <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {a.attendees.join(", ")}
                                  </p>
                                )}
                                {a.deadline && (
                                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Deadline: {formatTime(a.deadline)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
